import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FaCrown, FaFire, FaChevronLeft, FaChevronRight, FaArrowRight } from "react-icons/fa";
import { GoLocation } from "react-icons/go";
import { api, priceText, thumb, imgFallback } from "../api";
import { useAuth } from "../context/AuthContext";
import { trackPromoClick } from "../lib/boost";
import "./PromotedAds.css";

const PROMOTE_HREF = "/dashboard/promotions";
const loginNext = (to) => `/login?next=${encodeURIComponent(to)}`;

/* ── "Earn with TroveHub Affiliates" banner ─────────────────────────────── */
function AffiliateBanner() {
  const { isLoggedIn } = useAuth();
  const [cfg, setCfg] = useState({ percent: 20 });
  const [joined, setJoined] = useState(false);

  useEffect(() => { api("/affiliate/config").then(setCfg).catch(() => {}); }, []);
  useEffect(() => {
    if (!isLoggedIn) { setJoined(false); return; }
    let live = true;
    api("/me/affiliate").then((d) => live && setJoined(!!d.joined)).catch(() => {});
    return () => { live = false; };
  }, [isLoggedIn]);

  const to = "/dashboard/referrals";
  return (
    <div className="aff-banner">
      <span className="aff-icon" aria-hidden="true"><FaFire /></span>
      <div className="aff-text">
        <h3>Earn with TroveHub Affiliates</h3>
        <p>
          Get your referral link and earn {cfg.percent ? `${cfg.percent}% of TroveHub's fees ` : ""}
          when people you refer buy, sell or promote ads on TroveHub.
        </p>
      </div>
      <Link to={isLoggedIn ? to : loginNext(to)} className="aff-btn">
        {joined ? "View earnings" : "Join Program"} <FaArrowRight />
      </Link>
    </div>
  );
}

/* ── One promoted listing ───────────────────────────────────────────────── */
function Tile({ item }) {
  const id = item.id || item._id;
  const img = Array.isArray(item.images) && item.images[0] ? item.images[0] : "/placeholder.jpg";
  return (
    <Link to={`/product/${id}`} className="pa-tile" onClick={() => { trackPromoClick(item._id); window.scrollTo({ top: 0, behavior: "smooth" }); }}>
      <div className="pa-tile-img">
        <img src={thumb(img, 480)} alt={item.title || "Promoted listing"} loading="lazy" decoding="async" onError={imgFallback(img)} />
        <span className="pa-tile-tag"><FaCrown /> Ad</span>
      </div>
      <div className="pa-tile-body">
        <p className="pa-tile-name">{item.title}</p>
        {(item.city || item.address) && <p className="pa-tile-loc"><GoLocation size={12} /> {item.city || item.address}</p>}
        <p className="pa-tile-price">{priceText(item)}</p>
      </div>
    </Link>
  );
}

/* ── Promoted Ads carousel ──────────────────────────────────────────────── */
export default function PromotedAds() {
  const { isLoggedIn } = useAuth();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const rail = useRef(null);
  const [edge, setEdge] = useState({ start: true, end: true });

  useEffect(() => {
    let live = true;
    api("/promoted?limit=12").then((d) => live && setItems(Array.isArray(d) ? d : [])).catch(() => {}).finally(() => live && setLoaded(true));
    return () => { live = false; };
  }, []);

  const measure = useCallback(() => {
    const el = rail.current; if (!el) return;
    setEdge({ start: el.scrollLeft < 8, end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 8 });
  }, []);
  useEffect(() => { measure(); window.addEventListener("resize", measure); return () => window.removeEventListener("resize", measure); }, [measure, items, loaded]);

  const scroll = (dir) => { const el = rail.current; if (el) el.scrollBy({ left: dir * Math.max(240, el.clientWidth * 0.8), behavior: "smooth" }); };
  const promoteTo = isLoggedIn ? PROMOTE_HREF : loginNext(PROMOTE_HREF);

  return (
    <section className="pa-section">
      <div className="container">
        <AffiliateBanner />

        <div className="pa-card">
          <div className="pa-head">
            <span className="pa-icon" aria-hidden="true"><FaCrown /></span>
            <div className="pa-head-text">
              <div className="pa-title-row">
                <h2 className="pa-title">Promoted Ads</h2>
                <span className="pa-badge">Sponsored</span>
              </div>
              <p className="pa-sub">Popular listings and promotions across Nigeria</p>
            </div>
            <Link to={promoteTo} className="pa-promote">Promote your ad here <FaArrowRight /></Link>
          </div>

          <div className="pa-stage">
            {!edge.start && <button type="button" className="pa-arrow pa-arrow-l" onClick={() => scroll(-1)} aria-label="Previous"><FaChevronLeft /></button>}
            <div className="pa-rail" ref={rail} onScroll={measure}>
              {!loaded && [0, 1, 2].map((i) => <div key={i} className="pa-tile pa-skel" />)}

              {loaded && items.map((it) => <Tile key={it._id || it.id} item={it} />)}

              {loaded && items.length === 0 && (
                <Link to={promoteTo} className="pa-tile pa-empty">
                  <span className="pa-empty-plus">+</span>
                  <strong>Your ad could be here</strong>
                  <span>Get seen by thousands of buyers on the home page.</span>
                </Link>
              )}

              {loaded && (
                <div className="pa-cta">
                  <span className="pa-cta-icon"><FaCrown /></span>
                  <h3>Explore All Promoted Ads</h3>
                  <p>Discover listings from ID-checked sellers, backed by active TrovePay Escrow buyer protection.</p>
                  <Link to="/all" className="pa-cta-btn">View All Deals <FaArrowRight /></Link>
                </div>
              )}
            </div>
            {!edge.end && <button type="button" className="pa-arrow pa-arrow-r" onClick={() => scroll(1)} aria-label="Next"><FaChevronRight /></button>}
          </div>
        </div>
      </div>
    </section>
  );
}
